import { useState, useRef, useEffect } from 'react';
import './BrandsCarousel.scss';
import { desktopImages } from '../../../../images';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registrar plugin do GSAP
gsap.registerPlugin(ScrollTrigger);

const BrandsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);

  // Detectar mudança de tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animações GSAP para slides
  useEffect(() => {
    if (!isDesktop || !carouselRef.current) return;

    const carousel = carouselRef.current;
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());

    const timer = setTimeout(() => {
      // SLIDE 1
      const slide1 = carousel.querySelector('.slide-1');
      if (slide1) {
        const slide1Pharma = slide1.querySelector('.slide-main-image-1');
        const slide1PackPharma = slide1.querySelector('.slide-alt-image-1');
        const slide1Injetaveis = slide1.querySelector('.slide-injetaveis-image');
        gsap.set([slide1Pharma, slide1PackPharma, slide1Injetaveis], { scale: 0.85, opacity: 1 });
      }

      // SLIDE 2
      const slide2 = carousel.querySelector('.slide-2');
      if (slide2) {
        const logo = slide2.querySelector('.slide-logo');
        const title = slide2.querySelector('.slide-title');
        const description = slide2.querySelector('.slide-description');
        const cta = slide2.querySelector('.slide-cta');
        const mainImage = slide2.querySelector('.slide-main-image-2');
        const altImage = slide2.querySelector('.slide-alt-image-2');
        const looseText = slide2.querySelector('.loose-text');

        gsap.set(logo, { x: 80, opacity: 0 });
        gsap.set([title, description, cta], { y: 30, opacity: 0 });
        gsap.set([mainImage, altImage], { scale: 0.85, opacity: 1 });
        gsap.set(looseText, { scale: 1, opacity: 1 });
      }

      // SLIDE 3
      const slide3 = carousel.querySelector('.slide-3');
      if (slide3) {
        gsap.set(slide3.querySelector('.slide-logo'), { y: 60, opacity: 0 });
        gsap.set(slide3.querySelector('.slide-title'), { y: 50, opacity: 0 });
        gsap.set(slide3.querySelector('.slide-description'), { y: 40, opacity: 0 });
        gsap.set(slide3.querySelector('.slide-cta'), { y: 30, opacity: 0 });
        gsap.set(slide3.querySelector('.slide-main-image-3'), { scale: 0.85, opacity: 1 });
        gsap.set(slide3.querySelector('.slide-alt-image-3'), { scale: 0.85, x: 80, opacity: 1 });
        gsap.set(slide3.querySelector('.slide-products-image'), { scale: 0.85, opacity: 1 });
        gsap.set(slide3.querySelector('.slide-ewg-image'), { scale: 0.85, opacity: 1 });
        gsap.set(slide3.querySelector('.loose-text'), { y: 50, opacity: 1 });
      }

      // SLIDE 4
      const slide4 = carousel.querySelector('.slide-4');
      if (slide4) {
        gsap.set(slide4.querySelector('.slide-logo'), { x: 100, opacity: 0 });
        gsap.set(slide4.querySelector('.slide-title'), { x: 80, opacity: 0 });
        gsap.set(slide4.querySelector('.slide-description'), { x: 60, opacity: 0 });
        gsap.set(slide4.querySelector('.slide-cta'), { x: 50, opacity: 0 });
        gsap.set(slide4.querySelector('.slide-main-image-4'), { scale: 0.85, x: 120, opacity: 1 });
        gsap.set(slide4.querySelector('.slide-alt-image-4'), { scale: 0.85, x: 100, opacity: 1 });
      }

      // SLIDE 5
      const slide5 = carousel.querySelector('.slide-5');
      if (slide5) {
        gsap.set(slide5.querySelector('.slide-logo'), { x: 80, opacity: 0 });
        gsap.set(slide5.querySelector('.slide-title'), { x: 80, opacity: 0 });
        gsap.set(slide5.querySelector('.slide-description'), { x: 80, opacity: 0 });
        gsap.set(slide5.querySelector('.slide-cta'), { x: 80, opacity: 0 });
        gsap.set(slide5.querySelector('.slide-main-image-5'), { scale: 0.85, y: 80, opacity: 1 });
        gsap.set(slide5.querySelector('.slide-alt-image-5'), { scale: 0.85, x: -60, opacity: 1 });
        gsap.set(slide5.querySelector('.slide-caps-image'), { scale: 0.85, x: 100, opacity: 1 });
        gsap.set(slide5.querySelector('.loose-text'), { x: 80, opacity: 1 });
      }

      const handleCarouselScroll = () => {
        const scrollLeft = carousel.scrollLeft;
        const slideWidth = carousel.clientWidth;
        const slideGap = 120;

        // SLIDE 1 - Animações similares ao slide 2
        const slide1Start = 0;
        const slide1End = slideWidth * 0.8;
        const slide1ScrollProgress = Math.max(0, Math.min((scrollLeft - slide1Start) / (slide1End - slide1Start), 1));

        if (slide1) {
          const slide1Pharma = slide1.querySelector('.slide-main-image-1');
          const slide1PackPharma = slide1.querySelector('.slide-alt-image-1');
          const slide1Injetaveis = slide1.querySelector('.slide-injetaveis-image');

          // slide1Pharma - primeira imagem (começa em 0%)
          const pharmaProgress = Math.min(slide1ScrollProgress / 0.4, 1);
          const pharmaScale = 0.85 + (pharmaProgress * 0.15);
          gsap.to(slide1Pharma, { scale: pharmaScale, opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });

          // slide1PackPharma - segunda imagem (começa em 15%)
          const packProgress = Math.max(0, Math.min((slide1ScrollProgress - 0.15) / 0.45, 1));
          const packScale = 0.85 + (packProgress * 0.15);
          gsap.to(slide1PackPharma, { scale: packScale, opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });

          // slide1Injetaveis - terceira imagem (começa em 30%)
          const injetaveisProgress = Math.max(0, Math.min((slide1ScrollProgress - 0.3) / 0.5, 1));
          const injetaveisScale = 0.85 + (injetaveisProgress * 0.15);
          gsap.to(slide1Injetaveis, { scale: injetaveisScale, opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });
        }

        // SLIDE 2 - Logo e title juntos, depois description acelera e ultrapassa CTA
        const slide2Start = slideWidth * 0.5;
        const slide2End = slideWidth + slideGap + slideWidth * 0.8;
        const slide2ScrollProgress = Math.max(0, Math.min((scrollLeft - slide2Start) / (slide2End - slide2Start), 1));

        if (slide2) {
          const logo = slide2.querySelector('.slide-logo');
          const title = slide2.querySelector('.slide-title');
          const description = slide2.querySelector('.slide-description');
          const cta = slide2.querySelector('.slide-cta');
          const mainImage = slide2.querySelector('.slide-main-image-2');
          const altImage = slide2.querySelector('.slide-alt-image-2');
          const looseText = slide2.querySelector('.loose-text');

          // Logo e title começam juntos
          const logoProgress = Math.min(slide2ScrollProgress / 0.2, 1);
          gsap.to(logo, { x: 80 * (1 - logoProgress), opacity: logoProgress, duration: 0.2, ease: 'power2.out', overwrite: true });
          gsap.to(title, { y: 30 * (1 - logoProgress), opacity: logoProgress, duration: 0.2, ease: 'power2.out', overwrite: true });

          // Description acelera e ultrapassa
          const descProgress = Math.max(0, Math.min((slide2ScrollProgress - 0.15) / 0.2, 1));
          gsap.to(description, { y: 30 * (1 - descProgress), opacity: descProgress, duration: 0.2, ease: 'power2.out', overwrite: true });

          // CTA vem depois (mais cedo)
          const ctaProgress = Math.max(0, Math.min((slide2ScrollProgress - 0.25) / 0.15, 1));
          gsap.to(cta, { y: 30 * (1 - ctaProgress), opacity: ctaProgress, duration: 0.2, ease: 'power2.out', overwrite: true });

          // MainImage acelera e ultrapassa altImage
          const mainImgProgress = Math.max(0, Math.min((slide2ScrollProgress - 0.35) / 0.35, 1));
          const mainScale = 0.85 + (mainImgProgress * 0.15);
          gsap.to(mainImage, { scale: mainScale, opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });

          const altImgProgress = Math.max(0, Math.min((slide2ScrollProgress - 0.45) / 0.5, 1));
          const altScale = 0.85 + (altImgProgress * 0.15);
          gsap.to(altImage, { scale: altScale, opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });

          gsap.to(looseText, { opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });
        }

        // SLIDE 3 - Textos sobem de baixo para cima
        const slide3Start = (slideWidth + slideGap) * 1 + slideWidth * 0.5;
        const slide3End = (slideWidth + slideGap) * 2 + slideWidth * 0.8;
        const slide3ScrollProgress = Math.max(0, Math.min((scrollLeft - slide3Start) / (slide3End - slide3Start), 1));

        if (slide3) {
          // Logo sobe e para
          const logoProgress = Math.min(slide3ScrollProgress / 0.15, 1);
          gsap.to(slide3.querySelector('.slide-logo'), { y: 60 * (1 - logoProgress), opacity: logoProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Title sobe e para
          const titleProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.1) / 0.15, 1));
          gsap.to(slide3.querySelector('.slide-title'), { y: 50 * (1 - titleProgress), opacity: titleProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Description sobe e para
          const descProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.2) / 0.15, 1));
          gsap.to(slide3.querySelector('.slide-description'), { y: 40 * (1 - descProgress), opacity: descProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // CTA sobe e para (mais cedo)
          const ctaProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.25) / 0.15, 1));
          gsap.to(slide3.querySelector('.slide-cta'), { y: 30 * (1 - ctaProgress), opacity: ctaProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Products (slide3-noorskin1) primeiro com zoom
          const productsProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.38) / 0.22, 1));
          const productsScale = 0.85 + (productsProgress * 0.15);
          gsap.to(slide3.querySelector('.slide-products-image'), { scale: productsScale, duration: 0.3, ease: 'power1.out', overwrite: true });

          // AltImage (slide3-cremes) vem da direita com zoom
          const altImgProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.5) / 0.2, 1));
          const altScale = 0.85 + (altImgProgress * 0.15);
          gsap.to(slide3.querySelector('.slide-alt-image-3'), { scale: altScale, x: 80 * (1 - altImgProgress), duration: 0.3, ease: 'power1.out', overwrite: true });

          // MainImage (slide3-noorskin) por último com zoom
          const mainImgProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.62) / 0.2, 1));
          const mainScale = 0.85 + (mainImgProgress * 0.15);
          gsap.to(slide3.querySelector('.slide-main-image-3'), { scale: mainScale, duration: 0.3, ease: 'power1.out', overwrite: true });

          // EWG vem depois
          const ewgProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.65) / 0.18, 1));
          const ewgScale = 0.85 + (ewgProgress * 0.15);
          gsap.to(slide3.querySelector('.slide-ewg-image'), { scale: ewgScale, duration: 0.3, ease: 'power1.out', overwrite: true });

          // LooseText sobe por último
          const looseProgress = Math.max(0, Math.min((slide3ScrollProgress - 0.2) / 0.3, 1));
          gsap.to(slide3.querySelector('.loose-text'), { y: 50 * (1 - looseProgress), duration: 0.3, ease: 'none', overwrite: true });
        }

        // SLIDE 4 - Movimento da direita para esquerda
        const slide4Start = (slideWidth + slideGap) * 2 + slideWidth * 0.3;
        const slide4End = (slideWidth + slideGap) * 3 + slideWidth * 0.5;
        const slide4ScrollProgress = Math.max(0, Math.min((scrollLeft - slide4Start) / (slide4End - slide4Start), 1));

        if (slide4) {
          // Logo e title entram da direita juntos
          const logoTitleProgress = Math.min(slide4ScrollProgress / 0.2, 1);
          gsap.to(slide4.querySelector('.slide-logo'), { x: 100 * (1 - logoTitleProgress), opacity: logoTitleProgress, duration: 0.3, ease: 'power2.out', overwrite: true });
          gsap.to(slide4.querySelector('.slide-title'), { x: 80 * (1 - logoTitleProgress), opacity: logoTitleProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Description acelera e ultrapassa
          const descProgress = Math.max(0, Math.min((slide4ScrollProgress - 0.12) / 0.18, 1));
          gsap.to(slide4.querySelector('.slide-description'), { x: 60 * (1 - descProgress), opacity: descProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // CTA vem depois
          const ctaProgress = Math.max(0, Math.min((slide4ScrollProgress - 0.28) / 0.17, 1));
          gsap.to(slide4.querySelector('.slide-cta'), { x: 50 * (1 - ctaProgress), opacity: ctaProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // MainImage acelera e ultrapassa com zoom
          const mainImgProgress = Math.max(0, Math.min((slide4ScrollProgress - 0.4) / 0.3, 1));
          const mainScale = 0.85 + (mainImgProgress * 0.15);
          gsap.to(slide4.querySelector('.slide-main-image-4'), { scale: mainScale, x: 120 * (1 - mainImgProgress), opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });

          // AltImage vem depois com zoom
          const altImgProgress = Math.max(0, Math.min((slide4ScrollProgress - 0.6) / 0.4, 1));
          const altScale = 0.85 + (altImgProgress * 0.15);
          gsap.to(slide4.querySelector('.slide-alt-image-4'), { scale: altScale, x: 100 * (1 - altImgProgress), opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: true });
        }

        // SLIDE 5 - Textos vem da direita um por um
        const slide5Start = (slideWidth + slideGap) * 3 + slideWidth * 0.5;
        const slide5End = (slideWidth + slideGap) * 4 + slideWidth * 0.3;
        const slide5ScrollProgress = Math.max(0, Math.min((scrollLeft - slide5Start) / (slide5End - slide5Start), 1));

        if (slide5) {
          // Logo vem da direita e para
          const logoProgress = Math.min(slide5ScrollProgress / 0.15, 1);
          gsap.to(slide5.querySelector('.slide-logo'), { x: 80 * (1 - logoProgress), opacity: logoProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Title vem da direita e para
          const titleProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.12) / 0.15, 1));
          gsap.to(slide5.querySelector('.slide-title'), { x: 80 * (1 - titleProgress), opacity: titleProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // Description vem da direita e para
          const descProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.24) / 0.15, 1));
          gsap.to(slide5.querySelector('.slide-description'), { x: 80 * (1 - descProgress), opacity: descProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // CTA vem da direita e para
          const ctaProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.36) / 0.15, 1));
          gsap.to(slide5.querySelector('.slide-cta'), { x: 80 * (1 - ctaProgress), opacity: ctaProgress, duration: 0.3, ease: 'power2.out', overwrite: true });

          // MainImage desce com zoom
          const mainImgProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.48) / 0.22, 1));
          const mainScale = 0.85 + (mainImgProgress * 0.15);
          gsap.to(slide5.querySelector('.slide-main-image-5'), { scale: mainScale, y: 80 * (1 - mainImgProgress), duration: 0.3, ease: 'none', overwrite: true });

          // AltImage acelera da esquerda
          const altImgProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.6) / 0.2, 1));
          const altScale = 0.85 + (altImgProgress * 0.15);
          gsap.to(slide5.querySelector('.slide-alt-image-5'), { scale: altScale, x: -60 * (1 - altImgProgress), duration: 0.3, ease: 'none', overwrite: true });

          // Caps vem da direita
          const capsProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.72) / 0.18, 1));
          const capsScale = 0.85 + (capsProgress * 0.15);
          gsap.to(slide5.querySelector('.slide-caps-image'), { scale: capsScale, x: 100 * (1 - capsProgress), duration: 0.3, ease: 'none', overwrite: true });

          // LooseText vem da direita
          const looseProgress = Math.max(0, Math.min((slide5ScrollProgress - 0.15) / 0.25, 1));
          gsap.to(slide5.querySelector('.loose-text'), { x: 80 * (1 - looseProgress), duration: 0.3, ease: 'none', overwrite: true });
        }
      };

      carousel.addEventListener('scroll', handleCarouselScroll, { passive: true });

      return () => {
        carousel.removeEventListener('scroll', handleCarouselScroll);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isDesktop]);

  // Configurar scroll vertical controlando movimento horizontal
  useEffect(() => {
    if (!isDesktop || !carouselRef.current) return;

    const carousel = carouselRef.current;
    const container = containerRef.current;

    const handleWheelScroll = (e) => {
      const rect = container.getBoundingClientRect();
      const isCarouselVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

      if (!isCarouselVisible) return;

      const scrollLeft = carousel.scrollLeft;
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

      if (scrollLeft <= 5 && e.deltaY < 0) return;
      if (scrollLeft >= (maxScrollLeft - 5) && e.deltaY > 0) return;

      e.preventDefault();

      // Scroll suave com inércia
      const scrollAmount = e.deltaY * 1.2;
      gsap.to(carousel, {
        scrollLeft: scrollLeft + scrollAmount,
        duration: 0.5,
        ease: 'power2.out'
      });
    };

    container.addEventListener('wheel', handleWheelScroll, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelScroll);
    };
  }, [isDesktop]);

  // Dados dos slides com informações das marcas
  const slidesData = [
    {
      id: 'essentia-pharma',
      name: 'ESSENTIA PHARMA',
      logo: desktopImages.EssentiaPharmaDark,
      mainImage: desktopImages.Slide1Pharma,
      altImage: desktopImages.Slide1PackPharma,
      title: 'CIÊNCIA, PRECISÃO E\nCUIDADO PERSONALIZADO.',
      description: 'Na Essentia Pharma, unimos inovação farmacêutica, controle rigoroso de processos e tecnologia para transformar prescrições em soluções feitas sob medida para cada pessoa. Nossos laboratórios seguem padrões industriais e nossas tecnologias garantem precisão, eficácia e pureza para os tratamentos.',
      cta: 'Conheça a excelência\n nos medicamentos\n personalizados',
      url: 'https://essentia.com.br/'
    },
    {
      id: 'essential-nutrition',
      name: 'ESSENTIAL NUTRITION',
      logo: desktopImages.EssentialNutritionDark,
      mainImage: desktopImages.Slide2Nutrition,
      altImage: desktopImages.Slide2Produtos,
      title: 'Nutrição funcional\n de alta performance.',
      description: 'Na Essential Nutrition, qualidade é o resultado da união entre sabedoria, conhecimento sobre o corpo, tecnologia avançada e os melhores ingredientes do mundo. A partir disso, criamos fórmulas inteligentes, saborosas e de alto valor nutricional. São produtos que trazem ingredientes em sinergia, potencializando benefícios através da combinação dos nutrientes corretos.',
      cta: 'Explore a performance\n da nutrição inteligente',
      url: 'https://www.essentialnutrition.com.br/'
    },
    {
      id: 'noorskin',
      name: 'NOORSKIN',
      logo: desktopImages.NoorskinDark,
      mainImage: desktopImages.Slide3Noorskin,
      altImage: desktopImages.Slide3Cremes,
      title: 'Beleza limpa com\n resultados clínicos.',
      description: 'Cleanical é a evolução da beleza limpa, com tecnologia, experiência sensorial e resultados visíveis na pele. Na Noorskin, mais do que escolher ingredientes limpos e seguros, aplicamos testes de alta performance para garantir a eficácia clínica dos nossos produtos. Unimos ciência e segurança para entregar resultados reais, com o mesmo rigor que aplicamos em tudo o que fazemos.',
      cta: 'Descubra a beleza\n com propósito',
      url: 'https://www.noorskin.com.br/'
    },
    {
      id: 'essentia-technologies',
      name: 'ESSENTIA TECHNOLOGIES',
      logo: desktopImages.TechnologiesDark,
      mainImage: desktopImages.Slide4Tech,
      altImage: desktopImages.Slide4Easy,
      title: 'A evolução\n do cuidado.',
      description: 'Na Essentia Technologies, desenvolvemos soluções para elevar o cuidado com o ser humano. Com o Easy Health, unimos tecnologias avançadas de inteligência artificial para tornar a gestão clínica mais prática, o acompanhamento mais preciso e o relacionamento entre prescritor e paciente ainda mais humano. É tecnologia feita para quem escolheu cuidar.',
      cta: 'Conheça como a tecnologia\nimpulsiona o cuidado',
      url: 'https://easy-health.app/landing-page/index.html'
    },
    {
      id: 'be-generous',
      name: 'BE GENEROUS',
      logo: desktopImages.BeGenerousDark,
      mainImage: desktopImages.Slide5BeGenerous,
      altImage: desktopImages.Slide5Bebe,
      title: 'Qualidade que transforma\n o presente e nutre o futuro.',
      description: 'Be Generous é o nosso projeto social que doa suplementos pré-natais para gestantes com maior necessidade nutricional. Os suplementos do Be Generous são produzidos com a mesma qualidade aplicada em todos os nossos produtos e chegam às gestantes em várias partes do Brasil em forma de cuidado, ciência e amor.',
      cta: 'Conheça nosso\n projeto social',
      url: 'https://begenerous.com.br/'
    }
  ];

  // Navegação básica
  const goToSlide = (index) => {
    setCurrentSlide(index);
    if (carouselRef.current && isDesktop) {
      const carousel = carouselRef.current;
      const slideWidth = carousel.clientWidth;
      const slideGap = 120; // Gap entre slides definido no CSS

      // Calcular posição considerando o gap entre slides
      const scrollPosition = index * (slideWidth + slideGap);

      carousel.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Função para próximo slide
  const nextSlide = () => {
    const nextIndex = (currentSlide + 1) % slidesData.length;
    goToSlide(nextIndex);
  };

  // Funções para controlar o arrasto da barra de progresso
  const handleProgressBarMouseDown = (e) => {
    e.preventDefault();
    setIsDraggingProgress(true);

    const progressBar = progressBarRef.current;
    if (!progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressBarWidth = rect.width;

    // Calcular qual slide baseado na posição do clique
    const slideIndex = Math.round((clickX / progressBarWidth) * (slidesData.length - 1));
    const validSlideIndex = Math.max(0, Math.min(slideIndex, slidesData.length - 1));

    goToSlide(validSlideIndex);
  };

  const handleProgressDotMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Evitar que o clique na barra seja acionado
    setIsDraggingProgress(true);

    const startX = e.clientX;
    const startSlide = currentSlide;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const progressBar = progressBarRef.current;
      if (!progressBar) return;

      const progressBarWidth = progressBar.getBoundingClientRect().width;
      const slideWidth = progressBarWidth / slidesData.length;

      // Calcular novo slide baseado no movimento
      const slideOffset = Math.round(deltaX / slideWidth);
      const newSlide = Math.max(0, Math.min(startSlide + slideOffset, slidesData.length - 1));

      if (newSlide !== currentSlide) {
        goToSlide(newSlide);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingProgress(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Detectar mudança de slide baseada no scroll
  useEffect(() => {
    if (!isDesktop || !carouselRef.current) return;

    const carousel = carouselRef.current;

    const handleScrollDetection = () => {
      const scrollLeft = carousel.scrollLeft;
      const slideWidth = carousel.clientWidth;
      const slideGap = 120;
      const totalSlideWidth = slideWidth + slideGap;
      const slideProgress = scrollLeft / totalSlideWidth;
      const fractionalPart = slideProgress - Math.floor(slideProgress);

      let newSlide = currentSlide;

      if (fractionalPart > 0.7) {
        newSlide = Math.min(Math.ceil(slideProgress), slidesData.length - 1);
      } else if (fractionalPart < 0.3) {
        newSlide = Math.max(Math.floor(slideProgress), 0);
      }

      const validSlide = Math.max(0, Math.min(newSlide, slidesData.length - 1));

      if (validSlide !== currentSlide) {
        setCurrentSlide(validSlide);
      }
    };

    carousel.addEventListener('scroll', handleScrollDetection, { passive: true });

    return () => {
      carousel.removeEventListener('scroll', handleScrollDetection);
    };
  }, [currentSlide, isDesktop, slidesData.length]);

  // Tornar elementos do slide atual visíveis
  useEffect(() => {
    if (!carouselRef.current) return;

    const carousel = carouselRef.current;
    const currentSlideElement = carousel.querySelector(`.slide-${currentSlide + 1}`);

    if (!currentSlideElement) return;

    const elements = currentSlideElement.querySelectorAll('.slide-logo, .slide-title, .slide-description, .slide-cta, [class*="slide-main-image"], [class*="slide-alt-image"], .slide-injetaveis-image, .slide-products-image, .slide-ewg-image, .slide-caps-image, .loose-text');

    elements.forEach(el => {
      gsap.to(el, {
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out'
      });
    });
  }, [currentSlide]);

  return (
    <div className="brands-carousel" >
      {/* Header */}
      <div className="carousel-header">
        <h2 className="carousel-title">
          Nossas marcas: qualidade que se transforma em cuidado
        </h2>
      </div>
      <div className="container-sticky">
        {/* Container principal */}
        <div className="carousel-container" ref={containerRef}>
          {/* Slides individuais */}
          <div className="carousel-slides" ref={carouselRef}>

            {/* SLIDE 1 - ESSENTIA PHARMA */}
            <div className={`carousel-slide slide-1 ${!isDesktop && currentSlide !== 0 ? 'hidden' : ''}`}>
              <div className="slide-layout">
                {/* Coluna Esquerda - Padrão */}
                <div className="slide-left-column">
                  <img src={slidesData[0].logo} alt={slidesData[0].name} className="slide-logo" />
                  <h3 className="slide-title">{slidesData[0].title}</h3>
                  <p className="slide-description">{slidesData[0].description}</p>
                  <a
                    href={slidesData[0].url}
                    target="_blank"
                    rel="noreferrer"
                    className="slide-cta"
                  >
                    {slidesData[0].cta}
                  </a>
                </div>

                {/* Coluna Direita - Personalizada */}
                <div className="slide-right-column">
                  {/* Conteúdo personalizado do Slide 1 */}
                  <div className="slide-custom-content">
                    <img width={417} height={521} src={slidesData[0].mainImage} alt="Essentia Pharma" className="slide-main-image-1" />
                    <img width={501} height={208} src={slidesData[0].altImage} alt="Produtos Essentia Pharma" className="slide-alt-image-1" />
                    <img width={204} height={307} src={desktopImages.Slide1LaboratorioInjetaveis} alt="Laboratório Injetáveis Essentia Pharma" className="slide-injetaveis-image" />
                    <p className='loose-text'>Fórmulas únicas porque somos únicos.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SLIDE 2 - ESSENTIAL NUTRITION */}
            <div className={`carousel-slide slide-2 ${!isDesktop && currentSlide !== 1 ? 'hidden' : ''}`}>
              <div className="slide-layout">
                {/* Coluna Esquerda - Padrão */}
                <div className="slide-left-column">
                  <img src={slidesData[1].logo} alt={slidesData[1].name} className="slide-logo" />
                  <h3 className="slide-title">{slidesData[1].title}</h3>
                  <p className="slide-description">{slidesData[1].description}</p>
                  <a
                    href={slidesData[1].url}
                    target="_blank"
                    rel="noreferrer"
                    className="slide-cta"
                  >
                    {slidesData[1].cta}
                  </a>
                </div>

                {/* Coluna Direita - Personalizada */}
                <div className="slide-right-column">
                  {/* Conteúdo personalizado do Slide 2 */}
                  <div className="slide-custom-content">
                    <img width={689} height={459} src={slidesData[1].mainImage} alt="Essential Nutrition" className="slide-main-image-2" />
                    <img width={509} height={317} src={slidesData[1].altImage} alt="Produtos Essential Nutrition" className="slide-alt-image-2" />
                    <p className='loose-text'>Enhanced <br /> Formulas</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SLIDE 3 - NOORSKIN */}
            <div className={`carousel-slide slide-3 ${!isDesktop && currentSlide !== 2 ? 'hidden' : ''}`}>
              <div className="slide-layout">
                {/* Coluna Esquerda - Padrão */}
                <div className="slide-left-column">
                  <img src={slidesData[2].logo} alt={slidesData[2].name} className="slide-logo" />
                  <h3 className="slide-title">{slidesData[2].title}</h3>
                  <p className="slide-description">{slidesData[2].description}</p>
                  <a
                    href={slidesData[2].url}
                    target="_blank"
                    rel="noreferrer"
                    className="slide-cta"
                  >
                    {slidesData[2].cta}
                  </a>
                </div>

                {/* Coluna Direita - Personalizada */}
                <div className="slide-right-column">
                  {/* Conteúdo personalizado do Slide 3 */}
                  <div className="slide-custom-content">
                    <img width={252} height={377} src={slidesData[2].mainImage} alt="Noorskin" className="slide-main-image-3" />
                    <img width={353} height={332} src={slidesData[2].altImage} alt="Produtos Noorskin" className="slide-alt-image-3" />
                    <img width={550} height={421} src={desktopImages.Slide3Noorskin1} alt="Noorskin Produtos" className="slide-products-image" />
                    <img width={84} height={84} src={desktopImages.Slide3Ewg} alt="EWG Verified Noorskin" className="slide-ewg-image" />
                    <p className='loose-text'>Be free From <br /> toxic load</p>
                  </div>
                </div>
              </div>
            </div>

            {/* SLIDE 4 - ESSENTIA TECHNOLOGIES */}
            <div className={`carousel-slide slide-4 ${!isDesktop && currentSlide !== 3 ? 'hidden' : ''}`}>
              <div className="slide-layout">
                {/* Coluna Esquerda - Padrão */}
                <div className="slide-left-column">
                  <img src={slidesData[3].logo} alt={slidesData[3].name} className="slide-logo" />
                  <h3 className="slide-title">{slidesData[3].title}</h3>
                  <p className="slide-description">{slidesData[3].description}</p>
                  <a
                    href={slidesData[3].url}
                    target="_blank"
                    rel="noreferrer"
                    className="slide-cta"
                  >
                    {slidesData[3].cta}
                  </a>
                </div>

                {/* Coluna Direita - Personalizada */}
                <div className="slide-right-column">
                  {/* Conteúdo personalizado do Slide 4 */}
                  <div className="slide-custom-content">
                    <img width={279} height={454} src={slidesData[3].mainImage} alt="Essentia Technologies" className="slide-main-image-4" />
                    <img width={746} height={469} src={slidesData[3].altImage} alt="Easy Health" className="slide-alt-image-4" />
                  </div>
                </div>
              </div>
            </div>

            {/* SLIDE 5 - BE GENEROUS */}
            <div className={`carousel-slide slide-5 ${!isDesktop && currentSlide !== 4 ? 'hidden' : ''}`}>
              <div className="slide-layout">
                {/* Coluna Esquerda - Padrão */}
                <div className="slide-left-column">
                  <img src={slidesData[4].logo} alt={slidesData[4].name} className="slide-logo" />
                  <h3 className="slide-title">{slidesData[4].title}</h3>
                  <p className="slide-description">{slidesData[4].description}</p>
                  <a
                    href={slidesData[4].url}
                    target="_blank"
                    rel="noreferrer"
                    className="slide-cta"
                  >
                    {slidesData[4].cta}
                  </a>
                </div>

                {/* Coluna Direita - Personalizada */}
                <div className="slide-right-column">
                  {/* Conteúdo personalizado do Slide 5 */}
                  <div className="slide-custom-content">
                    <img width={691} height={466} src={slidesData[4].mainImage} alt="Be Generous" className="slide-main-image-5" />
                    <img width={238} height={373} src={slidesData[4].altImage} alt="Produtos Be Generous" className="slide-alt-image-5" />
                    <img width={445} height={299} src={desktopImages.Slide5CapsBeGenerous} alt="Cápsulas Be Generous" className="slide-caps-image" />
                    <p className='loose-text'>O nutriente mais <br /> poderoso do <br /> mundo é o amor.</p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Navegação inferior com nomes das empresas */}
          <div className="carousel-navigation">
            {/* Barra de navegação com bolinha móvel */}
            <div className="carousel-progress-dots">
              <div
                className={`progress-dots-container ${isDraggingProgress ? 'dragging' : ''}`}
                onMouseDown={handleProgressBarMouseDown}
                ref={progressBarRef}
              >
                <div
                  className={`progress-dot active ${isDraggingProgress ? 'dragging' : ''}`}
                  style={{
                    left: currentSlide === 0
                      ? `calc(${currentSlide} * (100% / ${slidesData.length}) + (100% / ${slidesData.length}) / 2 - 22.5px - 20px)`
                      : currentSlide === slidesData.length - 1
                        ? `calc(${currentSlide} * (100% / ${slidesData.length}) + (100% / ${slidesData.length}) / 2 - 22.5px + 40px)` // Ajuste maior para alinhar com BE GENEROUS
                        : `calc(${currentSlide} * (100% / ${slidesData.length}) + (100% / ${slidesData.length}) / 2 - 22.5px)`
                  }}
                  onMouseDown={handleProgressDotMouseDown}
                />
              </div>
            </div>

            {/* Nomes das empresas */}
            <div className="carousel-brands">
              {slidesData.map((slide, index) => (
                <button
                  key={slide.id}
                  className={`nav-button ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                >
                  {slide.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Botão Next Slide */}
      <button className="next-slide-btn" onClick={nextSlide}>
        <img src={desktopImages.NextSlide} alt="Próximo slide" />
      </button>
    </div>
  );
};

export default BrandsCarousel;